// src/pages/trading-portal/TradeExecution/AIAgentMultiDateGenerator.js
// The AI Agent Simulator's generator form: a From-To range, the trading days in it (any of them
// can be removed), a default pattern with per-date overrides, and the settings the single-date
// generator has. Which dates trade is the server's to say (a stock's weekend is judged in Chicago,
// as live, whatever the agent's timezone), so the form asks it (`/preview`) as it changes and lists
// the dates, bars and patterns it will make.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import * as MuiIcons from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import { TimeFrameLabels } from '../stockTradingEnums';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const fieldSx = {
    '& .MuiInputBase-root': { fontFamily, fontSize: '0.8rem' },
    '& .MuiInputLabel-root': { fontFamily, fontSize: '0.8rem' },
    '& .MuiFormHelperText-root': { fontFamily, fontSize: '0.68rem', mx: 0.5 },
    // The app's stylesheet makes every one-line input 1rem with 10px 15px padding.
    '& input.MuiInputBase-input': { fontSize: '0.8rem', padding: '8px 10px' },
};
const headCellSx = { fontFamily, fontSize: '0.72rem', fontWeight: 600, color: '#64748b', whiteSpace: 'nowrap', py: 1 };
const bodyCellSx = { fontFamily, fontSize: '0.75rem', py: 0.5 };
// The preview is asked for once the form has been still this long.
const PREVIEW_DELAY_MS = 350;
// The paths the form shows next to their own field; any other error goes in the box below.
const FIELD_PATHS = ['date_from', 'date_to', 'timeframe', 'warmup', 'start_price', 'volume', 'seed',
    'default.pattern', 'default.volatility_pct', 'default.trend_pct'];

const pad = (n) => String(n).padStart(2, '0');
const isoDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const weekday = (iso) => new Date(`${iso}T12:00:00Z`).toLocaleDateString(undefined, { weekday: 'short', timeZone: 'UTC' });
const number = (value) => (value === '' || value === null || value === undefined ? null : Number(value));

// This week's Monday to Friday: a stock's trading week, and a place to start for a pair.
const thisWeek = () => {
    const monday = new Date();
    monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
    const friday = new Date(monday);
    friday.setDate(monday.getDate() + 4);
    return [isoDate(monday), isoDate(friday)];
};

// The dialog's form: from a scenario's stored generator for a regenerate (which keeps the seed, so
// changing one setting changes only what it should), else from the catalog's defaults.
export const multiDateForm = (params, catalog, fractional, keepSeed) => {
    const p = params || {};
    const defaults = catalog?.defaults || {};
    const [from, to] = thisWeek();
    return {
        multi: true,
        date_from: p.date_from || from,
        date_to: p.date_to || to,
        exclude_dates: p.exclude_dates || [],
        default: {
            pattern: p.default?.pattern || 'DIP_AND_RECOVERY',
            volatility_pct: p.default?.volatility_pct ?? '',
            trend_pct: p.default?.trend_pct ?? 0,
        },
        days: p.days || {},
        timeframe: p.timeframe || defaults.timeframe || 'FIVE_MIN',
        warmup: p.warmup ?? defaults.warmup ?? 20,
        start_price: p.start_price ?? '',
        volume: p.volume ?? '',
        seed: keepSeed ? (p.seed ?? '') : '',
        options_levels: p.options_levels || (fractional ? 'NONE' : 'AUTO'),
        use_live_context: p.use_live_context ?? true,
        errors: {},
    };
};

// The request body. Removed dates and overrides outside the range are dropped (the range may have
// changed after them), and an override's blank fields are left out so the default applies.
export const multiDateBody = (form) => {
    const inRange = (day) => day >= form.date_from && day <= form.date_to;
    const days = {};
    Object.entries(form.days || {}).forEach(([day, override]) => {
        if (!inRange(day)) return;
        const out = {};
        if (override.pattern) out.pattern = override.pattern;
        if (number(override.volatility_pct) !== null) out.volatility_pct = number(override.volatility_pct);
        if (number(override.trend_pct) !== null) out.trend_pct = number(override.trend_pct);
        if (Object.keys(out).length) days[day] = out;
    });
    return {
        date_from: form.date_from || null,
        date_to: form.date_to || null,
        exclude_dates: (form.exclude_dates || []).filter(inRange),
        default: {
            pattern: form.default.pattern,
            volatility_pct: number(form.default.volatility_pct),
            trend_pct: number(form.default.trend_pct),
        },
        days,
        timeframe: form.timeframe,
        warmup: number(form.warmup),
        start_price: number(form.start_price),
        volume: number(form.volume),
        seed: number(form.seed),
        options_levels: form.options_levels,
        use_live_context: Boolean(form.use_live_context),
    };
};

const MultiDateGeneratorFields = ({ form, set, catalog, agent, api, optionsLabels }) => {
    const [preview, setPreview] = useState(null);       // {dates, warmup_bars, total_bars, errors}
    const [loading, setLoading] = useState(false);
    const seq = useRef(0);
    const body = useMemo(() => multiDateBody(form), [form]);
    const bodyRef = useRef(body);
    bodyRef.current = body;
    // What decides the dates and their patterns; the price, volume and seed do not.
    const previewKey = JSON.stringify([body.date_from, body.date_to, body.exclude_dates, body.default,
        body.days, body.timeframe, body.warmup]);

    useEffect(() => {
        const mine = ++seq.current;
        const handle = setTimeout(async () => {
            setLoading(true);
            try {
                const res = await axiosInstance.post(`${api}/preview`, { generator: bodyRef.current });
                if (mine !== seq.current) return;          // a newer form already asked
                setPreview({
                    ...res.data.data,
                    errors: (res.data.errors || []).map((e) => ({ ...e, path: e.path.replace(/^generator\./, '') })),
                });
            } catch (err) {
                if (mine !== seq.current) return;
                setPreview({ dates: [], warmup_bars: 0, total_bars: 0,
                    errors: [{ path: '', message: err?.response?.data?.message || 'The dates could not be worked out.' }] });
            } finally {
                if (mine === seq.current) setLoading(false);
            }
        }, PREVIEW_DELAY_MS);
        return () => clearTimeout(handle);
    }, [api, previewKey]);

    // A submit's errors win over the preview's for the same place.
    const errors = useMemo(() => {
        const out = { ...(form.errors || {}) };
        (preview?.errors || []).forEach((e) => { if (!(e.path in out)) out[e.path] = e.message; });
        return out;
    }, [form.errors, preview]);
    const err = (path) => errors[path];
    const otherErrors = Object.entries(errors)
        .filter(([path]) => !FIELD_PATHS.includes(path) && !/^days\.\d{4}-\d{2}-\d{2}\./.test(path));

    const patterns = catalog?.patterns || [];
    const patternLabel = (key) => patterns.find((p) => p.key === key)?.label || key;
    const defaultPattern = patterns.find((p) => p.key === form.default.pattern);
    const defaultVol = form.default.volatility_pct === '' ? catalog?.defaults?.volatility_pct?.[form.timeframe] : form.default.volatility_pct;
    const limits = catalog?.limits || {};
    const setDefault = (change) => set({ default: { ...form.default, ...change } });
    const setDay = (day, change) => set({ days: { ...form.days, [day]: { ...(form.days[day] || {}), ...change } } });
    const removed = (form.exclude_dates || []).filter((day) => day >= form.date_from && day <= form.date_to);
    const rows = preview?.dates || [];

    return (
        <>
            <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                    <TextField size="small" fullWidth label="From" type="date" value={form.date_from}
                        onChange={(e) => set({ date_from: e.target.value })} InputLabelProps={{ shrink: true }}
                        error={Boolean(err('date_from'))} helperText={err('date_from') || 'The first date'} sx={fieldSx} />
                </Grid>
                <Grid item xs={6} md={3}>
                    <TextField size="small" fullWidth label="To" type="date" value={form.date_to}
                        onChange={(e) => set({ date_to: e.target.value })} InputLabelProps={{ shrink: true }}
                        error={Boolean(err('date_to'))} helperText={err('date_to') || `A range of at most ${limits.max_range_days || 31} days`}
                        sx={fieldSx} />
                </Grid>
                <Grid item xs={12} md={6}>
                    <FormControl size="small" fullWidth sx={fieldSx} error={Boolean(err('timeframe'))}>
                        <InputLabel>Timeframe</InputLabel>
                        <Select label="Timeframe" value={form.timeframe} onChange={(e) => set({ timeframe: e.target.value })}>
                            {(catalog?.timeframes || []).map((tf) => <MenuItem key={tf} value={tf} sx={{ fontFamily, fontSize: '0.8rem' }}>{TimeFrameLabels[tf] || tf}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <Typography sx={{ fontFamily, fontSize: '0.72rem', color: err('timeframe') ? '#dc2626' : '#64748b', mt: 0.5 }}>
                        {err('timeframe') || `One session a date: the agent's trading window (${agent.trading_start_time && agent.trading_end_time
                            ? `${agent.trading_start_time}–${agent.trading_end_time}` : '24 hours'} ${agent.trading_timezone || 'America/Chicago'}).`}
                    </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                    <FormControl size="small" fullWidth sx={fieldSx} error={Boolean(err('default.pattern'))}>
                        <InputLabel>Pattern for every date</InputLabel>
                        <Select label="Pattern for every date" value={form.default.pattern} onChange={(e) => setDefault({ pattern: e.target.value })}>
                            {patterns.map((p) => <MenuItem key={p.key} value={p.key} sx={{ fontFamily, fontSize: '0.8rem' }}>{p.label}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <Typography sx={{ fontFamily, fontSize: '0.72rem', color: err('default.pattern') ? '#dc2626' : '#64748b', mt: 0.5 }}>
                        {err('default.pattern') || defaultPattern?.description}
                    </Typography>
                </Grid>
                <Grid item xs={6} md={4}>
                    <TextField size="small" fullWidth label="Volatility % per bar" type="number" value={form.default.volatility_pct}
                        onChange={(e) => setDefault({ volatility_pct: e.target.value })}
                        error={Boolean(err('default.volatility_pct'))}
                        helperText={err('default.volatility_pct') || `Blank: ${catalog?.defaults?.volatility_pct?.[form.timeframe] ?? '—'}%`} sx={fieldSx} />
                </Grid>
                <Grid item xs={6} md={4}>
                    <TextField size="small" fullWidth label="Drift % per bar" type="number" value={form.default.trend_pct}
                        onChange={(e) => setDefault({ trend_pct: e.target.value })} disabled={form.default.pattern !== 'RANDOM_WALK'}
                        error={Boolean(err('default.trend_pct'))} helperText={err('default.trend_pct') || 'Random walk only'} sx={fieldSx} />
                </Grid>
            </Grid>

            <Box sx={{ mt: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem' }}>Dates</Typography>
                    {loading && <CircularProgress size={12} />}
                </Box>
                <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#64748b', mb: 1 }}>
                    {rows.length
                        ? `${rows.length} trading date${rows.length === 1 ? '' : 's'}, run back to back: each opens near the last close and `
                            + 'positions carry overnight. '
                            + `${preview.warmup_bars} warm-up bars come from the session before the first date. `
                            + `${preview.total_bars.toLocaleString()} bars in all (at most ${(limits.max_bars || 2000).toLocaleString()}).`
                        : 'The trading days of the range appear here. A stock skips weekends; a crypto pair trades every day.'}
                </Typography>
                {rows.length > 0 && (
                    <TableContainer sx={{ maxHeight: 330, border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    {['Date', 'Bars', 'Pattern', 'Volatility %', 'Drift %', ''].map((h) => <TableCell key={h} sx={headCellSx}>{h}</TableCell>)}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((row) => {
                                    const override = form.days[row.date] || {};
                                    const pattern = override.pattern || form.default.pattern;
                                    const rowErr = (key) => err(`days.${row.date}.${key}`);
                                    return (
                                        <TableRow key={row.date} sx={{ bgcolor: Object.keys(override).some((k) => override[k] !== '' && override[k] !== undefined) ? '#fffbeb' : 'transparent' }}>
                                            <TableCell sx={{ ...bodyCellSx, whiteSpace: 'nowrap' }}>
                                                {row.date}
                                                <Typography component="span" sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8', ml: 0.75 }}>{weekday(row.date)}</Typography>
                                            </TableCell>
                                            <TableCell sx={bodyCellSx}>{row.bars}</TableCell>
                                            <TableCell sx={{ ...bodyCellSx, minWidth: 200 }}>
                                                <FormControl size="small" fullWidth sx={fieldSx} error={Boolean(rowErr('pattern'))}>
                                                    <Select value={override.pattern || ''} displayEmpty onChange={(e) => setDay(row.date, { pattern: e.target.value })}>
                                                        <MenuItem value="" sx={{ fontFamily, fontSize: '0.8rem' }}><em>Default: {patternLabel(form.default.pattern)}</em></MenuItem>
                                                        {patterns.map((p) => <MenuItem key={p.key} value={p.key} sx={{ fontFamily, fontSize: '0.8rem' }}>{p.label}</MenuItem>)}
                                                    </Select>
                                                </FormControl>
                                                {rowErr('pattern') && <Typography sx={{ fontFamily, fontSize: '0.68rem', color: '#dc2626', mt: 0.3 }}>{rowErr('pattern')}</Typography>}
                                            </TableCell>
                                            <TableCell sx={{ ...bodyCellSx, width: 130 }}>
                                                <TextField size="small" type="number" value={override.volatility_pct ?? ''} placeholder={String(defaultVol ?? '')}
                                                    onChange={(e) => setDay(row.date, { volatility_pct: e.target.value })}
                                                    error={Boolean(rowErr('volatility_pct'))} helperText={rowErr('volatility_pct') || undefined} sx={fieldSx} />
                                            </TableCell>
                                            <TableCell sx={{ ...bodyCellSx, width: 120 }}>
                                                <TextField size="small" type="number" value={override.trend_pct ?? ''} placeholder={String(form.default.trend_pct ?? 0)}
                                                    disabled={pattern !== 'RANDOM_WALK'} onChange={(e) => setDay(row.date, { trend_pct: e.target.value })}
                                                    error={Boolean(rowErr('trend_pct'))} helperText={rowErr('trend_pct') || undefined} sx={fieldSx} />
                                            </TableCell>
                                            <TableCell sx={{ ...bodyCellSx, width: 40 }}>
                                                <Tooltip title="Leave this date out">
                                                    <IconButton size="small" aria-label={`Remove ${row.date}`} disabled={rows.length <= 1}
                                                        onClick={() => set({ exclude_dates: [...(form.exclude_dates || []), row.date].sort() })}
                                                        sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}>
                                                        <MuiIcons.EventBusy sx={{ fontSize: '1rem' }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                {removed.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
                        <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#64748b' }}>Left out:</Typography>
                        {removed.map((day) => (
                            <Chip key={day} size="small" label={`${day} ${weekday(day)}`}
                                onDelete={() => set({ exclude_dates: form.exclude_dates.filter((d) => d !== day) })}
                                sx={{ fontFamily, fontSize: '0.7rem', height: 22 }} />
                        ))}
                    </Box>
                )}
            </Box>

            <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                    <TextField size="small" fullWidth label="Warm-up bars" type="number" value={form.warmup} onChange={(e) => set({ warmup: e.target.value })}
                        error={Boolean(err('warmup'))} helperText={err('warmup') || `History before the first date (0 to ${limits.max_warmup || 100})`} sx={fieldSx} />
                </Grid>
                <Grid item xs={6} md={3}>
                    <TextField size="small" fullWidth label="Start price" type="number" value={form.start_price} onChange={(e) => set({ start_price: e.target.value })}
                        error={Boolean(err('start_price'))} helperText={err('start_price') || 'Blank: the live price'} sx={fieldSx} />
                </Grid>
                <Grid item xs={6} md={3}>
                    <TextField size="small" fullWidth label="Volume per bar" type="number" value={form.volume} onChange={(e) => set({ volume: e.target.value })}
                        error={Boolean(err('volume'))} helperText={err('volume') || 'Blank: a typical volume'} sx={fieldSx} />
                </Grid>
                <Grid item xs={6} md={3}>
                    <TextField size="small" fullWidth label="Seed" type="number" value={form.seed} onChange={(e) => set({ seed: e.target.value })}
                        error={Boolean(err('seed'))} helperText={err('seed') || 'Same seed, same dates. Blank: random'} sx={fieldSx} />
                </Grid>
                <Grid item xs={12} md={6}>
                    <FormControl size="small" fullWidth sx={fieldSx}>
                        <InputLabel>Options levels</InputLabel>
                        <Select label="Options levels" value={form.options_levels} onChange={(e) => set({ options_levels: e.target.value })}>
                            {(catalog?.options_levels || ['AUTO', 'LIVE', 'NONE']).map((o) => <MenuItem key={o} value={o} sx={{ fontFamily, fontSize: '0.8rem' }}>{optionsLabels[o] || o}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                    <FormControlLabel
                        control={<Switch checked={Boolean(form.use_live_context)} onChange={(e) => set({ use_live_context: e.target.checked })} />}
                        label={<Typography sx={{ fontFamily, fontSize: '0.8rem' }}>Start from the agent&apos;s live market context (macro, SPY, crypto, flags)</Typography>}
                    />
                </Grid>
            </Grid>
            {otherErrors.length > 0 && (
                <Alert severity="error" sx={{ fontFamily, fontSize: '0.78rem', mt: 1 }}>{otherErrors[0][1]}</Alert>
            )}
        </>
    );
};

export default MultiDateGeneratorFields;
